import { useState, useEffect } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Input } from "@/components/ui/input";
import { modelsApi } from "@/lib/api";
import { adaptModel } from "@/lib/adapters";
import type { AdaptedModel } from "@/lib/types";
import { Search, Copy, Check, ChevronDown, ChevronUp, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { ANTHROPIC_PROXY_URL, OPENAI_PROXY_URL } from "@/const";

function formatLimit(v: number | null): string {
  return v == null ? "∞" : v.toLocaleString();
}

type Proto = "anthropic" | "openai-chat" | "openai-response";

// Each ScienceLLM speaks exactly one protocol; the access guide below is
// matched to that protocol.
function protoOf(sl: AdaptedModel): Proto {
  return sl.protocol === "openai-response" ? "openai-response"
    : sl.protocol === "openai-chat" ? "openai-chat"
    : "anthropic";
}

// Endpoints share the same public base URL as the snippet's base_url so the
// displayed Endpoint and the code example always point at the same host.
const ENDPOINTS: Record<Proto, { label: string; url: string }> = {
  anthropic: { label: "Anthropic", url: `${ANTHROPIC_PROXY_URL}/v1/messages` },
  "openai-chat": { label: "OpenAI Chat", url: `${OPENAI_PROXY_URL}/v1/chat/completions` },
  "openai-response": { label: "OpenAI Responses", url: `${OPENAI_PROXY_URL}/v1/responses` },
};

function snippetFor(model: AdaptedModel): { lang: string; code: string } {
  const proto = protoOf(model);
  if (proto === "openai-chat") {
    return {
      lang: "Python",
      code: `import os
from openai import OpenAI

client = OpenAI(
    api_key=os.environ["SCIENCETOKEN_API_KEY"],
    base_url="${OPENAI_PROXY_URL}/v1"
)

resp = client.chat.completions.create(
    model="${model.id}",
    max_tokens=1024,
    messages=[{"role": "user", "content": "..."}]
)

print(resp)`,
    };
  }
  if (proto === "openai-response") {
    return {
      lang: "Python",
      code: `import os
from openai import OpenAI

client = OpenAI(
    api_key=os.environ["SCIENCETOKEN_API_KEY"],
    base_url="${OPENAI_PROXY_URL}/v1"
)

resp = client.responses.create(
    model="${model.id}",
    input="..."
)

print(resp)`,
    };
  }
  return {
    lang: "Python",
    code: `import os
import anthropic

client = anthropic.Anthropic(
    api_key=os.environ["SCIENCETOKEN_API_KEY"],
    base_url="${ANTHROPIC_PROXY_URL}"
)

message = client.messages.create(
    model="${model.id}",
    max_tokens=1024,
    messages=[{"role": "user", "content": "..."}]
)

print(message)`,
  };
}

function EndpointRow({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white/3 border border-white/8">
      <span className="text-[10px] text-slate-500 uppercase tracking-wider w-16 flex-shrink-0">{label}</span>
      <code className="flex-1 min-w-0 truncate font-mono text-[11px] text-slate-300">{url}</code>
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
        className="p-1 rounded text-slate-600 hover:text-slate-300 hover:bg-white/5 flex-shrink-0"
      >
        {copied ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
      </button>
    </div>
  );
}

function ModelRow({ model }: { model: AdaptedModel }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const { language } = useLanguage();

  return (
    <div className={cn("border-b border-white/5 last:border-0", expanded && "bg-white/2")}>
      {/* Main Row */}
      <div
        className="flex items-center gap-4 px-5 py-4 hover:bg-white/3 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-xs font-bold text-blue-400 flex-shrink-0">
          {model.providerShort}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white">{model.name}</span>
            {model.isFeatured && <Star size={11} className="text-amber-400 fill-amber-400" />}
            {model.isNew && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">NEW</span>}
            {model.status === "unavailable" && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-500/20 text-slate-400">inactive</span>
            )}
          </div>
          <div className="text-xs text-slate-500">{model.provider}</div>
        </div>
        <div className="hidden md:flex items-center gap-1 flex-wrap max-w-[200px]">
          {model.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="tag-llm text-[9px] px-1.5 py-0.5 rounded-full">{tag}</span>
          ))}
        </div>
        <div className="text-right hidden sm:block">
          <div className="text-xs font-mono text-slate-300">{(model.contextLength / 1024).toFixed(0)}K ctx</div>
          <div className="text-[10px] text-slate-600">{formatLimit(model.rpmLimit)} RPM · {formatLimit(model.tpmLimit)} TPM</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(model.id); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
            className="p-1.5 rounded text-slate-600 hover:text-slate-300 hover:bg-white/5"
          >
            {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
          </button>
          {expanded ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-5 pb-5">
          {(model.description || model.descriptionEn) && (
            <p className="text-xs text-slate-400 leading-relaxed mb-3">
              {language === "en" ? model.descriptionEn : model.description}
            </p>
          )}

          {/* Each ScienceLLM speaks exactly one protocol; show only the
              matching endpoint + access snippet for it. */}
          <div className="mb-4 space-y-1.5">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">
              Endpoint · <span className="text-slate-400 normal-case tracking-normal">{model.protocol}</span>
            </div>
            {(() => {
              const ep = ENDPOINTS[protoOf(model)];
              return <EndpointRow label={ep.label} url={ep.url} />;
            })()}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Model Info */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Model ID</span>
                <code className="font-mono text-slate-300">{model.id}</code>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Context Length</span>
                <span className="text-slate-300">{model.contextLength.toLocaleString()} tokens</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">RPM</span>
                <span className="text-blue-400 font-mono">{formatLimit(model.rpmLimit)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">TPM</span>
                <span className="text-cyan-400 font-mono">{formatLimit(model.tpmLimit)}</span>
              </div>
            </div>

            {/* Code Snippet */}
            {(() => {
              const snip = snippetFor(model);
              return (
                <div className="code-block">
                  <div className="px-3 py-1.5 border-b border-white/10 text-[10px] text-slate-500">{snip.lang}</div>
                  <pre className="p-3 text-[11px] font-mono text-slate-300 overflow-x-auto leading-relaxed">
{snip.code}
                  </pre>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ConsoleLLMs() {
  const [search, setSearch] = useState("");
  const [models, setModels] = useState<AdaptedModel[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    modelsApi
      .list()
      .then((list) => setModels(list.map(adaptModel)))
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Failed to load models");
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = models.filter(
    (m) =>
      !search ||
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.id.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <ConsoleLayout
      title="Science LLMs"
      subtitle="Manage and test science large language models"
    >
      <div className="p-6 space-y-5">
        {/* Search */}
        <div className="relative max-w-[360px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Search models..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white/5 border-white/10 text-sm h-9 text-white placeholder:text-slate-600"
          />
        </div>

        {/* Models List */}
        <div className="sci-card overflow-hidden">
          <div className="px-5 py-3 border-b border-white/8 grid grid-cols-[32px_1fr_200px_120px_80px] gap-4 text-[10px] text-slate-600 uppercase tracking-wider items-center">
            <div />
            <div>Model</div>
            <div className="hidden md:block">Tags</div>
            <div className="hidden sm:block text-right">Limits</div>
            <div />
          </div>
          {loading ? (
            <div className="px-5 py-10 text-center text-xs text-slate-500">Loading models…</div>
          ) : filtered.length === 0 ? (
            <div className="px-5 py-10 text-center text-xs text-slate-500">
              {models.length === 0
                ? "暂无可用模型,请联系管理员配置供应商白名单"
                : "No models match your search."}
            </div>
          ) : (
            filtered.map((model) => <ModelRow key={model.id} model={model} />)
          )}
        </div>
      </div>
    </ConsoleLayout>
  );
}
